import {
  useGetEnrollmentJointSuitabilityData,
  useSubmitEnrollmentJointSuitabilityData,
  type EnrollmentJointSuitabilitySubmitInput,
} from '@egw/api'
import { Button, GenericError, Input, ModalDrawer, ProgressBar, RadioGroup, useModalState, Tag } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { EnrollmentJointLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { useCallback, useEffect, type FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { InformationCircleIcon as InfoIconOutline } from '@heroicons/react/24/outline'
import { UserIcon } from '@heroicons/react/16/solid'
import { WEALTH_ROUTES } from '@egw/constants'
import { PageHeader } from '../../../components'
import { ALL_ENROLLMENT_ROUTES } from '../../../constants'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { useHandleEnrollmentErrors } from '../../../utils/joint/api-errors/enrollment-error-handler'
import { ROUTES as ROUTES_JOINT } from '../../../constants/routes/routes-joint'

const log = new Logger('Enrollment - Joint - Suitability')

interface FormData {
  isBrokerDealerEmployer?: number | undefined
  isDirectorPolicyMaker?: number | undefined
  isPoliticallyExposedPerson?: number | undefined
  affiliatedPoliticalOrganizations?: string | undefined
  hasPoliticallyExposedImmediateFamilyMembers?: number | undefined
  politicallyExposedImmediateFamilyMembers?: string | undefined
  directorPolicyMakerOfCompany?: string | undefined
}

export const SuitabilityPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()

  const { isOpen, openModal, closeModal } = useModalState()

  const { data, isLoading, error } = useGetEnrollmentJointSuitabilityData()
  const { mutate: submit, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentJointSuitabilityData()

  // Log initial data loading errors
  useEffect(() => {
    if (error) {
      log.error('Error loading enrollment joint suitability data', error)
    }
  }, [error])

  // Log form submission errors
  useEffect(() => {
    if (submitError) {
      log.error('Error submitting enrollment joint suitability form', submitError)
    }
  }, [submitError])

  const progress = data?.activePerson?.progress
  const profileName = data?.activePerson?.name?.firstName ? `${data.activePerson.name.firstName}'s Profile` : ''

  // Protect route
  useValidateRoute(data?.activePerson?.path)

  // Error handler for enrollment errors
  const handleEnrollmentErrors = useHandleEnrollmentErrors('Joint - Suitability')

  // Helper function to convert boolean to number for form
  const booleanToFormValue = useCallback((value: boolean | undefined): number | undefined => {
    if (value === undefined) return undefined
    return value ? 1 : 0
  }, [])

  // AUTO GENERATED FORM HANDLERS START
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors: formErrors, isValid, touchedFields },
  } = useForm<FormData>({
    defaultValues: {
      isBrokerDealerEmployer: undefined,
      isDirectorPolicyMaker: undefined,
      isPoliticallyExposedPerson: undefined,
      affiliatedPoliticalOrganizations: undefined,
      hasPoliticallyExposedImmediateFamilyMembers: undefined,
      politicallyExposedImmediateFamilyMembers: undefined,
    },
    mode: 'onChange',
  })

  // Update form values when data is loaded
  useEffect(() => {
    if (data?.activePerson) {
      reset({
        isBrokerDealerEmployer: booleanToFormValue(data.activePerson.isBrokerDealerEmployer),
        isDirectorPolicyMaker: booleanToFormValue(data.activePerson.isDirectorPolicyMaker),
        isPoliticallyExposedPerson: booleanToFormValue(data.activePerson.isPoliticallyExposedPerson),
        affiliatedPoliticalOrganizations: data.activePerson.affiliatedPoliticalOrganizations?.join(', ') ?? undefined,
        hasPoliticallyExposedImmediateFamilyMembers: booleanToFormValue(
          data.activePerson.hasPoliticallyExposedImmediateFamilyMembers,
        ),
        politicallyExposedImmediateFamilyMembers:
          data.activePerson.politicallyExposedImmediateFamilyMembers?.join(', ') ?? undefined,
      })
    }
  }, [data, reset, booleanToFormValue])

  const isShowCompanyNameInput = watch('isDirectorPolicyMaker')
  const isShowBrokerDealerInput = watch('isBrokerDealerEmployer')
  const showPoliticallyExposedPersonInput = watch('isPoliticallyExposedPerson')
  const showPoliticallyExposedPersonFamilyInput = watch('hasPoliticallyExposedImmediateFamilyMembers') === 1

  // Scroll to the input when the family members input is shown
  useEffect(() => {
    if (showPoliticallyExposedPersonFamilyInput) {
      const element = document.querySelector('input[name="politicallyExposedImmediateFamilyMembers"]')
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [showPoliticallyExposedPersonFamilyInput])

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData

    const payload: EnrollmentJointSuitabilitySubmitInput = {
      activePerson: {
        isBrokerDealerEmployer: Boolean(formData.isBrokerDealerEmployer),
        isDirectorPolicyMaker: Boolean(formData.isDirectorPolicyMaker),
        isPoliticallyExposedPerson: Boolean(formData.isPoliticallyExposedPerson),
        affiliatedPoliticalOrganizations: formData.isPoliticallyExposedPerson
          ? formData.affiliatedPoliticalOrganizations?.split(',').map((org) => org.trim())
          : undefined,
        hasPoliticallyExposedImmediateFamilyMembers: Boolean(formData.hasPoliticallyExposedImmediateFamilyMembers),
        politicallyExposedImmediateFamilyMembers: formData.hasPoliticallyExposedImmediateFamilyMembers
          ? formData.politicallyExposedImmediateFamilyMembers?.split(',').map((member) => member.trim())
          : undefined,
        directorPolicyMakerOfCompany: formData.isDirectorPolicyMaker
          ? formData.directorPolicyMakerOfCompany
          : undefined,
      },
    }

    try {
      const response = await submit(payload)

      // Check for API errors in the response
      if (response && handleEnrollmentErrors(response)) {
        return
      }

      if (response?.activePerson?.path) {
        void navigate(response.activePerson.path)
      } else if (!response?.activePerson?.path) {
        log.error('No path returned from backend, redirecting to error page')
        void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path}`)
      }
    } catch (err) {
      log.error('Error submitting suitability form', err)
    }
  })

  return (
    <EnrollmentJointLayout title={t('joint.suitability.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <div className="w-full flex-1 flex flex-col justify-start">
          {/* AUTO GENERATED COMPONENTS START*/}
          {profileName ? (
            <Tag icon={<UserIcon className="text-olive-400" />} className="mb-xs">
              {profileName}
            </Tag>
          ) : null}
          <PageHeader
            title={t('joint.suitability.title')}
            className="mb-xs"
            // backPath={`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES_JOINT.INVESTMENT_GOALS.path}`}
          />
          <ProgressBar showAnimation progressPercentage={progress} className="mb-md" />
          <p className="typo-primary-base text-text-meta mb-xl">{t('joint.suitability.description')}</p>

          <form onSubmit={(e) => void onSubmit(e)} className="w-full flex-1 flex flex-col justify-start">
            <p className="typo-primary-base-semibold mb-2xs">{t('joint.suitability.broker_dealer_title')}</p>
            <p className="typo-primary-base-regular text-text-meta mb-sm">
              {t('joint.suitability.broker_dealer_description')}
            </p>
            <Controller
              name="isBrokerDealerEmployer"
              control={control}
              rules={{
                required: true,
                validate: (value: unknown) => (typeof value === 'number' && !isNaN(value)) || 'Field is required',
              }}
              render={({ field: { ref, ...restField } }) => (
                <RadioGroup
                  {...restField}
                  value={restField.value}
                  options={[
                    { value: 0, label: t('common.no') },
                    { value: 1, label: t('common.yes') },
                  ]}
                  name="isBrokerDealerEmployer"
                  testId="is-broker-dealer-radio"
                  className="!py-xs"
                />
              )}
            />
            {isShowBrokerDealerInput ? (
              <p className="typo-primary-base-regular text-text-body mt-sm">
                {t('joint.suitability.is_yes_broker_dealer')}
              </p>
            ) : null}
            <p className="typo-primary-base-semibold mb-2xs mt-xl">{t('joint.suitability.major_share_holder_title')}</p>
            <p className="typo-primary-base-regular text-text-meta mb-sm">
              {t('joint.suitability.major_share_holder_description')}
            </p>
            <Controller
              name="isDirectorPolicyMaker"
              control={control}
              rules={{
                required: true,
                validate: (value) => (typeof value === 'number' && !isNaN(value)) || 'Field is required',
              }}
              render={({ field: { ref, ...restField } }) => (
                <RadioGroup
                  {...restField}
                  value={restField.value}
                  options={[
                    { value: 0, label: t('common.no') },
                    { value: 1, label: t('common.yes') },
                  ]}
                  name="isDirectorPolicyMaker"
                  testId="is-director-policy-maker-of-company-radio"
                  className="!py-xs"
                />
              )}
            />
            {isShowCompanyNameInput ? (
              <>
                <p className="typo-primary-base-regular text-text-body my-sm">
                  {t('joint.suitability.is_yes_major_share_holder')}
                </p>
                <Controller
                  name="directorPolicyMakerOfCompany"
                  control={control}
                  rules={{
                    required: true,
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder={t('joint.suitability.company_name_and_ticker')}
                      name="directorPolicyMakerOfCompany"
                      testId="enrollment-name-company-input"
                      error={formErrors.directorPolicyMakerOfCompany?.message}
                    />
                  )}
                />
              </>
            ) : null}
            <p className="typo-primary-base-semibold mt-xl mb-2xs">
              {t('joint.suitability.politically_exposed_person_title')}
            </p>
            <div className="mb-sm">
              <button
                className="flex items-center typo-primary-base-semibold text-text-action py-2xs mb-sm"
                onClick={() => {
                  openModal()
                }}
                type="button"
              >
                <span>{t('joint.suitability.politically_exposed_person_description_1')}</span>{' '}
                <InfoIconOutline
                  strokeWidth={2.5}
                  onClick={openModal}
                  className="ml-3xs w-4 h-4 inline cursor-pointer "
                />
              </button>
              <p className="typo-primary-base-regular text-text-meta mb-2xs">
                {t('joint.suitability.politically_exposed_person_description_2')}
              </p>
            </div>
            {/* Politically Exposed Person */}
            <Controller
              name="isPoliticallyExposedPerson"
              control={control}
              rules={{
                required: true,
                validate: (value) => (typeof value === 'number' && !isNaN(value)) || 'Field is required',
              }}
              render={({ field: { ref, ...restField } }) => (
                <RadioGroup
                  {...restField}
                  value={restField.value}
                  options={[
                    { value: 0, label: t('common.no') },
                    { value: 1, label: t('common.yes') },
                  ]}
                  name="isPoliticallyExposedPerson"
                  testId="is-politically-exposed-person_radio"
                  className="!pb-xs"
                />
              )}
            />
            {showPoliticallyExposedPersonInput ? (
              <>
                <p className="typo-primary-sm-semibold text-text-meta mb-3xs mt-2xs">
                  {t('joint.suitability.is_yes_politically_exposed_person')}
                </p>
                <Controller
                  name="affiliatedPoliticalOrganizations"
                  control={control}
                  rules={{
                    required: t('common.required_field'),
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder={t('joint.suitability.political_organizations')}
                      name="affiliatedPoliticalOrganizations"
                      testId="enrollment-name-company-input"
                      error={
                        touchedFields.affiliatedPoliticalOrganizations
                          ? formErrors.affiliatedPoliticalOrganizations?.message
                          : undefined
                      }
                      className="!pb-0"
                    />
                  )}
                />
              </>
            ) : null}

            {/* Family members yes/no */}
            <p className="typo-primary-base-regular text-text-meta mt-md mb-2xs">
              {t('joint.suitability.politically_exposed_person_family_description_1')}
            </p>
            <Controller
              name="hasPoliticallyExposedImmediateFamilyMembers"
              control={control}
              rules={{
                required: true,
                validate: (value) => (typeof value === 'number' && !isNaN(value)) || 'Field is required',
              }}
              render={({ field: { ref, ...restField } }) => (
                <RadioGroup
                  {...restField}
                  value={restField.value}
                  options={[
                    { value: 0, label: t('common.no') },
                    { value: 1, label: t('common.yes') },
                  ]}
                  name="hasPoliticallyExposedImmediateFamilyMembers"
                  testId="is-politically-exposed-person-family_radio"
                  className="!py-xs"
                />
              )}
            />
            {/* Family members comma separated */}
            {showPoliticallyExposedPersonFamilyInput ? (
              <>
                <p className="typo-primary-sm-semibold text-text-meta mt-2xs mb-3xs">
                  {t('joint.suitability.is_yes_family_members')}
                </p>
                <Controller
                  name="politicallyExposedImmediateFamilyMembers"
                  control={control}
                  rules={{
                    required: t('common.required_field'),
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder={t('joint.suitability.family_members_placeholder')}
                      name="politicallyExposedImmediateFamilyMembers"
                      testId="politically-exposed-immediate-family-members-input"
                      error={
                        touchedFields.politicallyExposedImmediateFamilyMembers
                          ? formErrors.politicallyExposedImmediateFamilyMembers?.message
                          : undefined
                      }
                      isPII
                    />
                  )}
                />
              </>
            ) : null}

            <p className="typo-primary-sm-light text-text-meta mt-sm mb-xl">{t('joint.suitability.attestment')}</p>
            <Button
              type="submit"
              className="w-full md:w-min"
              isDisabled={!isValid}
              isLoading={isSubmitting}
              testId="enrollment-continue-button"
            >
              {t('common.continue')}
            </Button>
          </form>
          {/* AUTO GENERATED COMPONENTS END*/}
        </div>
      ) : null}

      <ModalDrawer title={t('joint.suitability.modal_title')} isOpen={isOpen} onClose={closeModal}>
        <div className="typo-primary-base-light text-text-body">
          <p>{t('joint.suitability.modal_description_1')}</p>
          <ul>
            <li className="list-disc list-inside ml-2">{t('joint.suitability.modal_ul_1')}</li>
            <li className="list-disc list-inside ml-2">{t('joint.suitability.modal_ul_2')}</li>
            <li className="list-disc list-inside ml-2">{t('joint.suitability.modal_ul_3')}</li>
            <li className="list-disc list-inside ml-2">{t('joint.suitability.modal_ul_4')}</li>
            <li className="list-disc list-inside ml-2">{t('joint.suitability.modal_ul_5')}</li>
            <li className="list-disc list-inside ml-2">{t('joint.suitability.modal_ul_6')}</li>
          </ul>
          <p className="mt-md">{t('joint.suitability.modal_description_2')}</p>
        </div>
      </ModalDrawer>

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentJointLayout>
  )
}
